import { createContext, useContext, useState, ReactNode } from "react";

export type Language = "en" | "ta";

const translations = {
  en: {
    // Navigation
    home: "Home",
    trips: "Trips",
    earnings: "Earnings",
    profile: "Profile",
    report: "Report",

    // Profile menu
    my_profile: "My Profile",
    notifications: "Notifications",
    theme: "Theme",
    language: "Language",
    settings: "Settings",
    sign_out: "Sign Out",
    dark_mode: "Dark mode",
    light_mode: "Light mode",
    manage_alerts: "Manage your alerts",
    app_preferences: "App preferences & support",
    log_out: "Log out of your account",
    english: "English",
    tamil: "Tamil",
    select_language: "Select your language",

    // Driver info
    driver_information: "Driver Information",
    driver_app: "Driver App",
    phone: "Phone",
    driver_id: "Driver ID",
    vehicle_reg: "Vehicle Reg. No.",
    vehicle_id: "Vehicle ID",
    online: "Online",
    offline: "Offline",

    // Trip status
    head_to_pickup: "Head to pickup",
    customer_waiting: "Customer waiting — Start trip",
    trip_in_progress: "Trip in Progress",

    // Actions
    accept: "Accept",
    decline: "Decline",
    reached: "I've Reached",
    start_trip: "Start Trip",
    end_trip: "End Trip",
    cancel_trip: "Cancel Trip",
    cancel: "Cancel",
    confirm: "Confirm",
    submit: "Submit",
    skip: "Skip",
    call: "Call",

    // Trip page
    trip_history: "Trip History",
    all: "All",
    completed: "Completed",
    cancelled: "Cancelled",
    no_trips: "No trips found",
    complete_first_trip: "Complete your first trip to see it here",
    loading_trips: "Loading trips…",

    // Earnings page
    net_earnings: "Net Earnings",
    total_collection: "Total Collection",
    today: "Today",
    week: "Week",
    month: "Month",
    this_week: "This Week",
    penalties: "Penalties",
    gross_earnings: "Gross earnings",
    net: "Net",
    commission: "commission",
    transaction_history: "Transaction History",
    no_transactions: "No transactions for",
    loading_earnings: "Loading earnings…",
    trip_earning: "Trip",
    bonus: "Bonus",
    penalty: "Penalty",
    deduction: "Deduction",

    // Performance page
    performance_report: "Performance Report",
    performance: "Performance",
    ratings: "Ratings",
    total: "Total",
    total_bookings: "Total Bookings",
    completed_trips: "Completed Trips",
    cancelled_trips: "Cancelled Trips",
    worked_hours: "Worked Hours",
    total_hours: "Total Hours",
    online_hours: "Online Hours",
    on_trip_hours: "On-Trip Hours",
    commission_rate: "Commission Rate",
    rating_breakdown: "Rating Breakdown",
    excellent: "Excellent",
    good: "Good",
    average: "Average",
    below_avg: "Below Avg",
    poor: "Poor",
    based_on: "Based on",
    ratings_label: "ratings",
    loading_report: "Loading report…",

    // Notifications
    no_notifications: "No notifications yet",
    notifications_empty_sub: "You'll see trip alerts and updates here",
    loading_notifications: "Loading notifications…",

    // Trip completion
    trip_completed: "Trip Completed!",
    final_fare: "Final Fare",
    cash_collect: "Cash • Collect from customer",
    received_cash: "Received Cash",
    rate_customer: "Rate Customer",
    submit_rating: "Submit Rating",
    distance: "Distance",
    duration: "Duration",
    payment: "Payment",

    // OTP & Opening KM
    enter_otp: "Enter OTP",
    enter_otp_sub: "Ask your customer for the 4-digit OTP",
    verify_otp: "Verify & Start Trip",
    enter_opening_km: "Enter Opening KM",
    opening_km_sub: "Record your odometer reading before starting",
    odometer_reading: "Odometer reading (km)",
    save_continue: "Save & Continue",

    // Cancel trip
    cancel_reason: "Cancel Reason",
    select_reason: "Select reason",
    cancel_confirm: "Are you sure you want to cancel?",

    // Availability
    go_online: "Go Online",
    go_offline: "Go Offline",
    today_trips: "Today's Trips",
    today_earnings: "Today's Earnings",

    // Common
    loading: "Loading…",
    no_data: "No data",
    safe_track: "Safe Track",
    driver_app_version: "Driver App v1.0.0",
    transaction: "transaction",
    transactions: "transactions",
    hrs: "hrs",
    km: "km",
    min: "min",
    cash: "Cash",
    vehicle_breakdown: "Vehicle Breakdown",
    personal_emergency: "Personal Emergency",
    customer_unreachable: "Customer Unreachable",
    other: "Other",
    not_logged_in: "You are not logged in",
  },
  ta: {
    // Navigation
    home: "முகப்பு",
    trips: "பயணங்கள்",
    earnings: "வருமானம்",
    profile: "சுயவிவரம்",
    report: "அறிக்கை",

    // Profile menu
    my_profile: "என் சுயவிவரம்",
    notifications: "அறிவிப்புகள்",
    theme: "தீம்",
    language: "மொழி",
    settings: "அமைப்புகள்",
    sign_out: "வெளியேறு",
    dark_mode: "இருண்ட பயன்முறை",
    light_mode: "ஒளி பயன்முறை",
    manage_alerts: "உங்கள் எச்சரிக்கைகளை நிர்வகிக்கவும்",
    app_preferences: "செயலி விருப்பங்கள் & ஆதரவு",
    log_out: "உங்கள் கணக்கிலிருந்து வெளியேறு",
    english: "ஆங்கிலம்",
    tamil: "தமிழ்",
    select_language: "உங்கள் மொழியை தேர்ந்தெடுக்கவும்",

    // Driver info
    driver_information: "சாரதி தகவல்",
    driver_app: "சாரதி செயலி",
    phone: "தொலைபேசி",
    driver_id: "சாரதி ID",
    vehicle_reg: "வாகன பதிவு எண்",
    vehicle_id: "வாகன ID",
    online: "இணையில்",
    offline: "இணையற்று",

    // Trip status
    head_to_pickup: "பிக்கப் நோக்கி செல்லவும்",
    customer_waiting: "வாடிக்கையாளர் காத்திருக்கிறார் — பயணம் தொடங்கவும்",
    trip_in_progress: "பயணம் நடக்கிறது",

    // Actions
    accept: "ஏற்றுக்கொள்",
    decline: "மறு",
    reached: "சென்றடைந்தேன்",
    start_trip: "பயணம் தொடங்கு",
    end_trip: "பயணம் முடி",
    cancel_trip: "பயணம் ரத்து",
    cancel: "ரத்து",
    confirm: "உறுதிப்படுத்து",
    submit: "சமர்ப்பி",
    skip: "தவிர்",
    call: "அழை",

    // Trip page
    trip_history: "பயண வரலாறு",
    all: "அனைத்தும்",
    completed: "முடிந்தது",
    cancelled: "ரத்தானது",
    no_trips: "பயணங்கள் இல்லை",
    complete_first_trip: "உங்கள் முதல் பயணத்தை முடிக்கவும்",
    loading_trips: "பயணங்கள் ஏற்றுகிறது…",

    // Earnings page
    net_earnings: "நிகர வருமானம்",
    total_collection: "மொத்த வசூல்",
    today: "இன்று",
    week: "வாரம்",
    month: "மாதம்",
    this_week: "இந்த வாரம்",
    penalties: "அபராதங்கள்",
    gross_earnings: "மொத்த வருமானம்",
    net: "நிகர",
    commission: "கமிஷன்",
    transaction_history: "பரிவர்த்தனை வரலாறு",
    no_transactions: "பரிவர்த்தனைகள் இல்லை",
    loading_earnings: "வருமானம் ஏற்றுகிறது…",
    trip_earning: "பயணம்",
    bonus: "போனஸ்",
    penalty: "அபராதம்",
    deduction: "கழிவு",

    // Performance page
    performance_report: "செயல்திறன் அறிக்கை",
    performance: "செயல்திறன்",
    ratings: "மதிப்பீடுகள்",
    total: "மொத்தம்",
    total_bookings: "மொத்த முன்பதிவுகள்",
    completed_trips: "முடிந்த பயணங்கள்",
    cancelled_trips: "ரத்தான பயணங்கள்",
    worked_hours: "பணி நேரம்",
    total_hours: "மொத்த நேரம்",
    online_hours: "ஆன்லைன் நேரம்",
    on_trip_hours: "பயண நேரம்",
    commission_rate: "கமிஷன் விகிதம்",
    rating_breakdown: "மதிப்பீடு விவரம்",
    excellent: "சிறந்தது",
    good: "நல்லது",
    average: "சராசரி",
    below_avg: "சராசரிக்கு கீழ்",
    poor: "மோசம்",
    based_on: "அடிப்படையில்",
    ratings_label: "மதிப்பீடுகள்",
    loading_report: "அறிக்கை ஏற்றுகிறது…",

    // Notifications
    no_notifications: "இன்னும் அறிவிப்புகள் இல்லை",
    notifications_empty_sub: "பயண எச்சரிக்கைகள் இங்கே தெரியும்",
    loading_notifications: "அறிவிப்புகள் ஏற்றுகிறது…",

    // Trip completion
    trip_completed: "பயணம் முடிந்தது!",
    final_fare: "இறுதி கட்டணம்",
    cash_collect: "பணம் • வாடிக்கையாளரிடம் பெறவும்",
    received_cash: "பணம் பெற்றேன்",
    rate_customer: "வாடிக்கையாளரை மதிப்பிடுக",
    submit_rating: "மதிப்பீடு சமர்ப்பி",
    distance: "தூரம்",
    duration: "கால அளவு",
    payment: "பணம்",

    // OTP & Opening KM
    enter_otp: "OTP உள்ளிடவும்",
    enter_otp_sub: "வாடிக்கையாளரிடம் 4 இலக்க OTP கேளுங்கள்",
    verify_otp: "சரிபார்த்து பயணம் தொடங்கு",
    enter_opening_km: "தொடக்க கிலோமீட்டர் உள்ளிடவும்",
    opening_km_sub: "தொடங்குமுன் ஓடோமீட்டர் வாசிப்பை பதிவு செய்யவும்",
    odometer_reading: "ஓடோமீட்டர் வாசிப்பு (கி.மீ)",
    save_continue: "சேமி & தொடர்",

    // Cancel trip
    cancel_reason: "ரத்து காரணம்",
    select_reason: "காரணம் தேர்ந்தெடு",
    cancel_confirm: "நீங்கள் ரத்து செய்ய விரும்புகிறீர்களா?",

    // Availability
    go_online: "இணையில் செல்",
    go_offline: "இணையை விட்டு வெளியேறு",
    today_trips: "இன்றைய பயணங்கள்",
    today_earnings: "இன்றைய வருமானம்",

    // Common
    loading: "ஏற்றுகிறது…",
    no_data: "தரவு இல்லை",
    safe_track: "ஸ்விஃப்ட் டாக்சி",
    driver_app_version: "சாரதி செயலி v1.0.0",
    transaction: "பரிவர்த்தனை",
    transactions: "பரிவர்த்தனைகள்",
    hrs: "மணி",
    km: "கி.மீ",
    min: "நிமிடம்",
    cash: "பணம்",
    vehicle_breakdown: "வாகன கோளாறு",
    personal_emergency: "தனிப்பட்ட அவசரநிலை",
    customer_unreachable: "வாடிக்கையாளரை அடைய முடியவில்லை",
    other: "மற்றவை",
    not_logged_in: "நீங்கள் உள்நுழையவில்லை",
  },
} as const;

type TranslationKey = keyof typeof translations.en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "app-language";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem(STORAGE_KEY) as Language) || "en";
    }
    return "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  const toggleLanguage = () => {
    setLanguage(language === "en" ? "ta" : "en");
  };

  const t = (key: TranslationKey): string => {
    return translations[language][key] ?? translations.en[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
